import { AgGrid, AgGridReact, Flex, Text } from "@jsq/ds/core";
import { TypedAGGridCellRenderParams } from "~/pages/Compliance/ComplianceTable/types";
import { type DocumentData } from "~/pages/Compliance/ProposedChangesModal/ValueCell";
import { type SelectionSource } from "./constants";
import { RadioValueCell } from "./RadioValueCell";
import * as styles from "./styles_vanilla.css";
import type { ComparisonRow } from "./types";

type GetSelection = (selectionGroupKey: string) => SelectionSource;
type SetSelection = (selectionGroupKey: string, source: SelectionSource) => void;

type GetColumnDefsOptions = {
  onDocumentClick?: (docId: number, profileId: number) => void;
  currentProfileId: number;
  publishedProfileId: number | null;
  proposedProfileId: number;
  getSelection: GetSelection;
  setSelection: SetSelection;
  hasPublished: boolean;
  readonly: boolean;
};

type ValueColumnConfig = {
  field: SelectionSource;
  title: string;
  subtitle: string;
  source: SelectionSource;
  profileId: number;
  getDocs: (row: ComparisonRow) => DocumentData[] | undefined;
};

type HeaderProps = { title: string; subtitle: string };

const ColumnHeader = ({ title, subtitle }: HeaderProps) => (
  <Flex flexDirection="column" gap="4px" className={styles.headerCell}>
    <Text variant="strong-sm">{title}</Text>
    <Text variant="secondary-sm" className={styles.headerSubtitle}>
      {subtitle}
    </Text>
  </Flex>
);

const isSelected =
  (source: SelectionSource, getSelection: GetSelection) =>
  (params: { data?: ComparisonRow }): boolean =>
    params.data ? getSelection(params.data.selectionGroupKey) === source : false;

const isRowDimmed = (row: ComparisonRow, source: SelectionSource): boolean =>
  row.dimmedMatches[source] !== undefined;

const canSelectSource = (
  row: ComparisonRow,
  source: SelectionSource,
  readonly: boolean,
): boolean =>
  !readonly &&
  row.renderRadio &&
  (source !== "recentlyPublished" || row.hasPublished) &&
  !isRowDimmed(row, source);

const isDimmed =
  (source: SelectionSource) =>
  (params: { data?: ComparisonRow }): boolean =>
    params.data ? isRowDimmed(params.data, source) : false;

const buildValueColumn = (
  config: ValueColumnConfig,
  options: GetColumnDefsOptions,
): AgGrid.ColDef<ComparisonRow> => ({
  field: config.field,
  headerName: config.title,
  headerComponent: ColumnHeader,
  headerComponentParams: { title: config.title, subtitle: config.subtitle },
  cellClass: styles.valueCell,
  cellClassRules: {
    [styles.selectedCell]: isSelected(config.source, options.getSelection),
    [styles.selectableCell]: (params) =>
      params.data ? canSelectSource(params.data, config.source, options.readonly) : false,
    [styles.dimmedCell]: isDimmed(config.source),
  },
  onCellClicked: (event) => {
    if (!event.data || !canSelectSource(event.data, config.source, options.readonly)) return;
    const target = event.event?.target as HTMLElement | undefined;
    if (target?.closest("button, a")) return;
    options.setSelection(event.data.selectionGroupKey, config.source);
  },
  cellRenderer: (params: TypedAGGridCellRenderParams<ComparisonRow>) => (
    <RadioValueCell
      row={params.data}
      source={config.source}
      value={String(params.value ?? "")}
      docs={config.getDocs(params.data)}
      profileId={config.profileId}
      onDocumentClick={options.onDocumentClick}
      readonly={options.readonly}
    />
  ),
});

const FieldCell = ({ value }: TypedAGGridCellRenderParams<ComparisonRow>) => (
  <Text variant="strong-md" className={styles.fieldText}>
    {String(value ?? "")}
  </Text>
);

export const getColumnDefs = (
  options: GetColumnDefsOptions,
): AgGridReact.AgGridReactProps<ComparisonRow>["columnDefs"] => {
  const columns: AgGrid.ColDef<ComparisonRow>[] = [
    {
      field: "field",
      headerName: "",
      width: 238,
      flex: 0,
      headerClass: styles.headerCellBorder,
      cellClass: styles.fieldCell,
      cellRenderer: FieldCell,
    },
    buildValueColumn(
      {
        field: "current",
        title: "Current",
        subtitle: "Profile data when case was created",
        source: "current",
        profileId: options.currentProfileId,
        getDocs: (row) => row.currentDocs,
      },
      options,
    ),
  ];

  if (options.hasPublished) {
    columns.push(
      buildValueColumn(
        {
          field: "recentlyPublished",
          title: "Recently published",
          subtitle: "Latest approved data from a closed case",
          source: "recentlyPublished",
          profileId: options.publishedProfileId ?? 0,
          getDocs: (row) => row.publishedDocs,
        },
        options,
      ),
    );
  }

  columns.push(
    buildValueColumn(
      {
        field: "proposed",
        title: "Proposed",
        subtitle: "Edits made in this case",
        source: "proposed",
        profileId: options.proposedProfileId,
        getDocs: (row) => row.proposedDocs,
      },
      options,
    ),
  );

  return columns;
};

export const gridOptions: AgGridReact.AgGridReactProps<ComparisonRow>["gridOptions"] = {
  suppressCellFocus: true,
  domLayout: "autoHeight",
  headerHeight: 72,
  rowHeight: 46,
  getRowId: (params) => params.data.rowKey,
  defaultColDef: {
    sortable: false,
    flex: 1,
    minWidth: 200,
    wrapText: true,
    autoHeight: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
    headerClass: styles.headerCellBorder,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
  },
};
